import Container from "@cloudscape-design/components/container"
import { diffWords, Change } from "diff"
import React from "react"

/**
 * Props for the DiffView component.
 */
interface DiffViewProps {
  /**
   * The original text to compare against.
   */
  original: string

  /**
   * The proposed/new text to compare.
   */
  proposed: string

  /**
   * Number of context lines to show around changes.
   *
   * @default 2
   */
  contextLines?: number
}

/**
 * Collapses unchanged sections, keeping only context around changes.
 */
function collapseUnchanged(
  changes: Change[],
  contextLines: number,
): (Change | { collapsed: true })[] {
  const result: (Change | { collapsed: true })[] = []
  const contextChars = contextLines * 80

  for (let i = 0; i < changes.length; i++) {
    const part = changes[i]

    if (part.added || part.removed) {
      result.push(part)
      continue
    }

    const prevIsChange = i > 0 && (changes[i - 1].added || changes[i - 1].removed)
    const nextIsChange = i < changes.length - 1 && (changes[i + 1].added || changes[i + 1].removed)

    if (part.value.length <= contextChars * 2) {
      result.push(part)
      continue
    }

    const suffix = prevIsChange ? part.value.slice(0, contextChars) : ""
    const prefix = nextIsChange ? part.value.slice(-contextChars) : ""

    if (suffix) result.push({ value: suffix, added: false, removed: false, count: 1 })
    result.push({ collapsed: true })
    if (prefix) result.push({ value: prefix, added: false, removed: false, count: 1 })
  }

  return result
}

const addedStyle: React.CSSProperties = {
  backgroundColor: "var(--color-background-status-success)",
  color: "var(--color-text-status-success)",
}

const removedStyle: React.CSSProperties = {
  backgroundColor: "var(--color-background-status-error)",
  color: "var(--color-text-status-error)",
  textDecoration: "line-through",
}

const collapsedStyle: React.CSSProperties = {
  fontStyle: "italic",
  color: "var(--color-text-status-inactive)",
}

const preStyle: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  fontFamily: "var(--font-family-monospace, monospace)",
  fontSize: "var(--font-body-s-size, 0.875rem)",
  lineHeight: "var(--font-body-s-line-height, 1.625)",
  margin: 0,
}

const scrollStyle: React.CSSProperties = {
  maxHeight: "16rem",
  overflow: "auto",
}

/**
 * Displays a word-level diff between two text values. Additions are highlighted in green, removals
 * in red with strikethrough. Unchanged sections are collapsed with context lines shown around
 * changes.
 */
const DiffView: React.FC<DiffViewProps> = ({ original, proposed, contextLines = 2 }) => {
  const changes = diffWords(original, proposed)
  const collapsed = collapseUnchanged(changes, contextLines)

  return (
    <Container>
      <div style={scrollStyle}>
        <pre style={preStyle}>
          {collapsed.map((part, index) => {
            if ("collapsed" in part) {
              return (
                <span key={index} style={collapsedStyle}>
                  {"\n···\n"}
                </span>
              )
            }

            if (part.added) {
              return (
                <span key={index} style={addedStyle} data-testid="diff-added">
                  {part.value}
                </span>
              )
            }

            if (part.removed) {
              return (
                <span key={index} style={removedStyle} data-testid="diff-removed">
                  {part.value}
                </span>
              )
            }

            return <span key={index}>{part.value}</span>
          })}
        </pre>
      </div>
    </Container>
  )
}

export default DiffView
